import { useEffect, useState } from "react";

import { motion, AnimatePresence } from "framer-motion";
import {
  Check, ArrowRight, ArrowLeft, Phone, MessageCircle, Mail, Facebook,
  Instagram, Globe, Upload, FileText, MessagesSquare, Package, PhoneCall,
  Loader2, PartyPopper,
} from "lucide-react";
import { Logo, StatusBadge } from "@/components/brand";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const STEPS = ["Business details", "Website knowledge", "Connect Channels", "Test & Start"];

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-200";

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="mb-1.5 block text-sm font-semibold text-slate-700">{label}</label>
      {children}
    </div>
  );
}

export function Step1({ profile, setProfile }) {
  const update = (field) => (e) => setProfile({ ...profile, [field]: e.target.value });
  return (
    <div className="grid gap-5 sm:grid-cols-2" data-testid="onboarding-step-1">
      <Field label="Business name" testid="field-business-name">
        <input data-testid="input-business-name" required placeholder="e.g. Sharma Electronics" value={profile.business_name} onChange={update("business_name")} className={inputCls} />
      </Field>
      <Field label="Website URL" testid="field-website-url">
        <input data-testid="input-website-url" placeholder="https://yourbusiness.com" value={profile.website_url} onChange={update("website_url")} className={inputCls} />
      </Field>
      <Field label="Business category" testid="field-business-category">
        <select data-testid="select-business-category" className={inputCls} value={profile.category} onChange={update("category")}>
          <option value="" disabled>Choose a category</option>
          <option>Retail / Shop</option><option>Online store</option><option>Services</option>
          <option>Restaurant / Food</option><option>Healthcare</option><option>Education</option><option>Other</option>
        </select>
      </Field>
      <Field label="Business location" testid="field-business-location">
        <input data-testid="input-business-location" placeholder="City, State" value={profile.business_location} onChange={update("business_location")} className={inputCls} />
      </Field>
      <Field label="Main contact number" testid="field-contact-number">
        <div className="flex">
          <span className="flex items-center rounded-l-xl border border-r-0 border-slate-200 bg-slate-50 px-3.5 text-sm font-medium text-slate-600">+91</span>
          <input data-testid="input-contact-number" inputMode="tel" placeholder="98XXX XXXXX" value={profile.contact_number} onChange={update("contact_number")} className={`${inputCls} rounded-l-none`} />
        </div>
      </Field>
      <Field label="Support email" testid="field-support-email">
        <input data-testid="input-support-email" type="email" placeholder="support@yourbusiness.com" value={profile.support_email} onChange={update("support_email")} className={inputCls} />
      </Field>
      <Field label="Preferred language" testid="field-preferred-language">
        <select data-testid="select-preferred-language" className={inputCls} value={profile.preferred_language} onChange={update("preferred_language")}>
          <option>English</option><option>हिंदी (Hindi)</option><option>Both English and Hindi</option>
        </select>
      </Field>
    </div>
  );
}

export function Step2({ profile, setProfile }) {
  const [importing, setImporting] = useState(false);
  const [importedPages, setImportedPages] = useState(0);
  const [websiteUrl, setWebsiteUrl] = useState(profile.website_url || "");
  const [activeTool, setActiveTool] = useState(null);
  const [entryTitle, setEntryTitle] = useState("");
  const [entryContent, setEntryContent] = useState("");
  const [documentFile, setDocumentFile] = useState(null);
  const [savingTool, setSavingTool] = useState(false);

  useEffect(() => {
    if (profile.website_url && !websiteUrl) setWebsiteUrl(profile.website_url);
  }, [profile.website_url, websiteUrl]);

  const doImport = async () => {
    if (!websiteUrl.trim()) {
      toast.error("Please enter your website URL.");
      return;
    }
    setImporting(true);
    try {
      const response = await fetch("/wp-json/csai/v1/knowledge/website", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ website_url: websiteUrl.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Could not import this website.");
      setImportedPages(Number(data.pages_imported || 0));
      setProfile((current) => ({ ...current, website_url: websiteUrl.trim() }));
      toast.success(`Website imported — ${data.pages_imported} page${data.pages_imported === 1 ? "" : "s"} found`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setImporting(false);
    }
  };

  const tools = [
    { icon: Upload, title: "Upload documents", desc: "Price lists, brochures, catalogues (PDF, Word)", type: "document" },
    { icon: FileText, title: "Add FAQs", desc: "Common questions and your answers", type: "faq" },
    { icon: MessagesSquare, title: "Import previous conversations", desc: "Old WhatsApp or email chats to learn your style", type: "conversation" },
    { icon: Package, title: "Add products or services", desc: "Names, prices, availability and details", type: "product" },
  ];

  const openTool = (tool) => {
    setActiveTool(tool);
    setEntryTitle("");
    setEntryContent("");
    setDocumentFile(null);
  };

  const saveTool = async () => {
    if (activeTool.type === "document" && !documentFile) {
      toast.error("Please choose a document.");
      return;
    }
    if (activeTool.type !== "document" && (!entryTitle.trim() || entryContent.trim().length < 10)) {
      toast.error("Add a title and at least 10 characters of information.");
      return;
    }
    setSavingTool(true);
    try {
      let response;
      if (activeTool.type === "document") {
        const formData = new FormData();
        formData.append("document", documentFile);
        response = await fetch("/wp-json/csai/v1/knowledge/document", {
          method: "POST", credentials: "same-origin", body: formData,
        });
      } else {
        response = await fetch("/wp-json/csai/v1/knowledge/text", {
          method: "POST", credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            knowledge_type: activeTool.type,
            title: entryTitle.trim(),
            content: entryContent.trim(),
          }),
        });
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Could not save this information.");
      toast.success(activeTool.type === "document"
        ? `${data.file_name} uploaded securely`
        : `${activeTool.title} saved`);
      setActiveTool(null);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingTool(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-step-2">
      <div className="rounded-2xl border-2 border-orange-200 bg-orange-50/40 p-6" data-testid="website-import-card">
        <p className="text-xs font-semibold uppercase tracking-wider text-orange-700">Recommended first step</p>
        <h3 className="mt-2 font-display text-lg font-bold text-slate-900">Import from your website</h3>
        <p className="mt-1 text-sm text-slate-600">
          We will read your website and prepare your business knowledge automatically.
        </p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input data-testid="import-website-input" type="url" placeholder="https://yourbusiness.com" value={websiteUrl} onChange={(event) => setWebsiteUrl(event.target.value)} className={`${inputCls} flex-1`} />
          <button
            data-testid="import-website-button"
            onClick={doImport}
            disabled={importing}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-orange-700 disabled:opacity-60"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
            {importing ? "Reading your website..." : "Import"}
          </button>
        </div>
        {importedPages > 0 && (
          <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="import-success-message">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <p className="text-sm text-emerald-800">
              {importedPages} website page{importedPages === 1 ? "" : "s"} imported. You can review the business knowledge before going live.
            </p>
          </div>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2" data-testid="teach-options-grid">
        {tools.map((o, i) => (
          <button
            key={o.title}
            data-testid={`teach-option-${i}`}
            onClick={() => openTool(o)}
            className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 text-left transition-all hover:border-orange-300 hover:shadow-md"
          >
            <div className="rounded-xl bg-slate-100 p-2.5 text-slate-600">
              <o.icon className="h-5 w-5" />
            </div>
            <div>
              <p className="font-display text-base font-bold text-slate-900">{o.title}</p>
              <p className="mt-0.5 text-sm text-slate-500">{o.desc}</p>
            </div>
          </button>
        ))}
      </div>

      {activeTool && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4" data-testid="knowledge-entry-dialog">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex items-start gap-4">
              <div className="rounded-xl bg-orange-50 p-2.5 text-orange-600">
                <activeTool.icon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-xl font-bold text-slate-900">{activeTool.title}</h3>
                <p className="mt-1 text-sm text-slate-500">{activeTool.desc}</p>
              </div>
            </div>

            {activeTool.type === "document" ? (
              <div className="mt-6">
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Choose document</label>
                <input
                  data-testid="knowledge-document-input"
                  type="file"
                  accept=".pdf,.docx,.txt,.csv"
                  onChange={(event) => setDocumentFile(event.target.files?.[0] || null)}
                  className="w-full rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-700 file:mr-4 file:rounded-lg file:border-0 file:bg-orange-100 file:px-4 file:py-2 file:font-semibold file:text-orange-800"
                />
                <p className="mt-2 text-xs text-slate-500">PDF, DOCX, TXT or CSV — maximum 10 MB.</p>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                <Field label={activeTool.type === "faq" ? "FAQ topic" : activeTool.type === "product" ? "Product or service name" : "Conversation source"}>
                  <input
                    data-testid="knowledge-title-input"
                    value={entryTitle}
                    onChange={(event) => setEntryTitle(event.target.value)}
                    placeholder={activeTool.type === "faq" ? "e.g. Delivery and returns" : activeTool.type === "product" ? "e.g. Premium support plan" : "e.g. WhatsApp customer chats"}
                    className={inputCls}
                  />
                </Field>
                <Field label={activeTool.type === "faq" ? "Questions and answers" : activeTool.type === "product" ? "Price, availability and full details" : "Paste previous conversations"}>
                  <textarea
                    data-testid="knowledge-content-input"
                    rows={8}
                    value={entryContent}
                    onChange={(event) => setEntryContent(event.target.value)}
                    placeholder="Add the information your AI should learn..."
                    className={inputCls}
                  />
                </Field>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                data-testid="knowledge-cancel-button"
                onClick={() => setActiveTool(null)}
                disabled={savingTool}
                className="rounded-xl border-2 border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                data-testid="knowledge-save-button"
                onClick={saveTool}
                disabled={savingTool}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:cursor-wait disabled:opacity-60"
              >
                {savingTool && <Loader2 className="h-4 w-4 animate-spin" />}
                {savingTool ? "Saving..." : activeTool.type === "document" ? "Upload securely" : "Save information"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const CHANNEL_ICONS = {
  talkid: Phone,
  whatsapp: MessageCircle,
  gmail: Mail,
  facebook: Facebook,
  instagram: Instagram,
  website: Globe,
};

const CHANNEL_LABELS = {
  not_connected: "Not connected",
  requested: "Queued",
  provisioning: "Connecting",
  waiting_for_login: "Waiting for login",
  connected: "Connected",
  needs_attention: "Needs attention",
  failed: "Failed",
};

export function Step3({dashboard=false}) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [connectionInputs, setConnectionInputs] = useState({});

  const updateChannel = (connection) => {
    setChannels((current) => current.map((item) =>
      item.channel === connection.channel ? connection : item
    ));
  };

  const loadChannels = async () => {
    try {
      const response = await fetch("/wp-json/csai/v1/channels", {
        credentials: "same-origin",
        cache: "no-store",
      });
      const data = await response.json();
      if (response.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (!response.ok) throw new Error(data.message || "Could not load channel status.");
      const liveChannels = Array.isArray(data.channels) ? data.channels : [];
      setChannels(liveChannels);
      setConnectionInputs((current) => {
        const next = { ...current };
        const gmail = liveChannels.find((item) => item.channel === "gmail");
        const browser = gmail?.methods?.find((item) => item.key === "browser");
        if (next["gmail:browser"] === undefined) {
          next["gmail:browser"] = browser?.details?.expected_identity || "";
        }
        return next;
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChannels();
    const timer = window.setInterval(loadChannels, 10000);
    const onConnected=e=>{if(e.origin===window.location.origin&&e.data?.type==="csai-channel-connected")loadChannels();};
    window.addEventListener("message",onConnected);
    return () => {window.clearInterval(timer);window.removeEventListener("message",onConnected);};
  }, []);

  const connectMethod = async (channel, method) => {
    if(method.available === false){toast.info("This connection setup is not available yet.");return;}
    const key = `${channel.channel}:${method.key}`;
    const expectedIdentity = (connectionInputs[key] || "").trim();
    const isGmailBrowser = channel.channel === "gmail" && method.key === "browser";
    if(method.key==="browser" && ["whatsapp","facebook","instagram"].includes(channel.channel)){
      const phone=expectedIdentity.replace(/[\s()-]/g,"");
      if(channel.channel==="whatsapp" && !/^\+[1-9]\d{7,14}$/.test(phone)){toast.error("Enter the WhatsApp number with country code, for example +91 followed by your number.");return;}
      const popup=window.open("about:blank","csai-"+channel.channel+"-login","popup=yes,width=1100,height=760,resizable=yes,scrollbars=yes");
      if(popup)popup.document.body.textContent="Opening your secure sign-in window…";
      const controller=new AbortController();const deadline=window.setTimeout(()=>controller.abort(),45000);
      setWorking(key);
      try{
        const access=await fetch("/wp-json/csai/v1/connection-access",{credentials:"same-origin",cache:"no-store",signal:controller.signal});const a=await access.json();if(!access.ok)throw Error(a.message||"Please sign in again.");
        const response=await fetch("/wp-json/csai/v1/browser-login",{method:"POST",credentials:"same-origin",headers:{"Content-Type":"application/json","X-CSAI-Connection-Nonce":a.nonce},body:JSON.stringify({channel:channel.channel,expected_identity:phone}),signal:controller.signal});
        const d=await response.json();if(!response.ok)throw Error(d.message||"Could not open sign-in.");
        if(popup){popup.location.replace(d.login_url);popup.focus();}else{window.location.href=d.login_url;}
        await loadChannels();
      }catch(e){if(popup&&!popup.closed)popup.close();toast.error(e.name==="AbortError"?"Sign-in took too long to open. Please try Configure again.":e.message);}finally{window.clearTimeout(deadline);setWorking(null);}
      return;
    }

    if (isGmailBrowser && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(expectedIdentity)) {
      toast.error("Enter the Gmail address this customer wants to connect.");
      return;
    }

    const loginWindow = isGmailBrowser
      ? window.open("about:blank", "csai-gmail-login", "popup=yes,width=1100,height=760,resizable=yes,scrollbars=yes")
      : null;
    setWorking(key);
    try {
      const response = await fetch(`/wp-json/csai/v1/channels/${channel.channel}/connect`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          connection_type: method.key,
          ...(isGmailBrowser ? { expected_identity: expectedIdentity } : {}),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Could not start this connection.");
      updateChannel(data.connection);

      if (isGmailBrowser) {
        const sessionResponse = await fetch("/wp-json/csai/v1/channels/gmail/login-session", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        const sessionData = await sessionResponse.json();
        if (!sessionResponse.ok) throw new Error(sessionData.message || "Could not open the secure Gmail login.");
        if (loginWindow) {
          loginWindow.location.replace(sessionData.login_url);
          loginWindow.focus();
          toast.info("Complete Google sign-in in the secure window. Your connection will be verified automatically.");
        } else {
          window.location.href = sessionData.login_url;
        }
      } else {
        toast.success(`${channel.name} — ${method.name} setup requested`);
      }
    } catch (error) {
      if (loginWindow && !loginWindow.closed) loginWindow.close();
      toast.error(error.message);
    } finally {
      setWorking(null);
    }
  };

  const verifyGmail = async (channel, method) => {
    const key = `${channel.channel}:${method.key}`;
    setWorking(key);
    try {
      const response = await fetch("/wp-json/csai/v1/channels/gmail/verify", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Gmail login could not be verified yet.");
      updateChannel(data.connection);
      toast.success("Gmail Normal Connection is connected and active.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setWorking(null);
    }
  };

  const activateMethod = async (channel, method) => {
    const key = `${channel.channel}:${method.key}`;
    setWorking(key);
    try {
      const response = await fetch(`/wp-json/csai/v1/channels/${channel.channel}/activate`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ connection_type: method.key }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Could not activate this connection.");
      updateChannel(data.connection);
      toast.success(`${method.name} is now active. The other connection remains securely saved but inactive.`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setWorking(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-12" data-testid="onboarding-step-3-loading">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-orange-600" />
        <span className="text-sm font-semibold text-slate-600">Checking live connections...</span>
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="onboarding-step-3">
      {channels.map((c, i) => {
        const Icon = CHANNEL_ICONS[c.channel] || Globe;
        const managedReady = c.kind === "managed" && (c.status === "connected" || Boolean(c.details?.public_url));
        return (
          <div key={c.channel} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5" data-testid={`channel-card-${i}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="rounded-xl bg-slate-100 p-2.5 text-slate-700"><Icon className="h-5 w-5" /></div>
              <StatusBadge label={managedReady ? "Connected" : (CHANNEL_LABELS[c.status] || "Not connected")} />
            </div>
            <p className="mt-4 font-display text-base font-bold text-slate-900">{c.name}</p>
            <p className="mt-0.5 text-sm text-slate-500">{c.description}</p>
            {dashboard && ["talkid","whatsapp","gmail"].includes(c.channel) && <a className="mt-3 inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800" href={"/dashboard?section=training&tab="+c.channel}>{c.channel==="gmail"?"Email":c.channel==="talkid"?"Calling":"WhatsApp"} automation</a>}

            {c.kind === "dual" && (
              <div className="mt-4 space-y-3">
                {(c.methods || []).map((method) => {
                  const key = `${c.channel}:${method.key}`;
                  const busy = working === key;
                  const pending = method.available !== false && ["requested", "provisioning"].includes(method.status) && !(method.key==="browser" && ["whatsapp","facebook","instagram"].includes(c.channel));
                  const connected = method.status === "connected";
                  return (
                    <div key={method.key} className={`rounded-xl border p-3 ${method.is_active ? "border-emerald-300 bg-emerald-50/60" : "border-slate-200 bg-slate-50/60"}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-bold text-slate-800">{method.name}</p>
                          <p className="mt-0.5 text-xs text-slate-500">{method.description}</p>
                        </div>
                        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                          method.is_active ? "bg-emerald-600 text-white" :
                          connected ? "bg-amber-100 text-amber-800" :
                          "bg-orange-100 text-orange-700"
                        }`}>
                          {method.is_active ? "Active" : connected ? "Connected · Inactive" : (CHANNEL_LABELS[method.status] || "Not connected")}
                        </span>
                      </div>
                      {c.channel === "whatsapp" && method.key === "browser" && !connected && <div className="mt-3"><label className="mb-1.5 block text-xs font-semibold text-slate-700">WhatsApp number to connect</label><input type="tel" value={connectionInputs[key] || ""} onChange={e=>setConnectionInputs(current=>({...current,[key]:e.target.value}))} placeholder="+91 9876543210" autoComplete="tel" className="w-full rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm"/><p className="mt-2 text-xs text-slate-500">Enter the new account number with country code, then scan the QR using that account’s Linked devices.</p></div>}
                      {c.channel === "gmail" && method.key === "browser" && !connected && (
                        <div className="mt-3">
                          <label className="mb-1.5 block text-xs font-semibold text-slate-700">Gmail address to connect</label>
                          <input
                            type="email"
                            value={connectionInputs[key] || ""}
                            onChange={(event) => setConnectionInputs((current) => ({ ...current, [key]: event.target.value }))}
                            placeholder="customer@gmail.com"
                            autoComplete="email"
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                          />
                          <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">Enter the Gmail account for this business. Password and OTP will be entered only on Google’s secure login page.</p>
                        </div>
                      )}
                      {method.details?.masked_identity && <p className="mt-2 text-xs font-medium text-slate-600">{method.details.masked_identity}</p>}
                      {method.details?.message && <p className="mt-2 text-xs leading-relaxed text-slate-500">{method.details.message}</p>}
                      {method.is_active ? (
                        <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><Check className="h-4 w-4" /> Connected and active</p>
                      ) : connected ? (
                        <button onClick={() => activateMethod(c, method)} disabled={busy} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Make active
                        </button>
                      ) : c.channel === "gmail" && method.key === "browser" && method.status === "waiting_for_login" ? (
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          <button onClick={() => connectMethod(c, method)} disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-orange-600 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60">
                            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Continue secure login
                          </button>
                          <button onClick={() => verifyGmail(c, method)} disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify Gmail connection
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => connectMethod(c, method)} disabled={busy || pending || method.available === false} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-orange-600 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:cursor-wait disabled:opacity-60">
                          {(busy || pending) && <Loader2 className="h-4 w-4 animate-spin" />}
                          {method.available === false ? "Not available yet" : pending ? "Preparing connection..." : method.status === "failed" ? "Retry configuration" : "Configure"}
                        </button>
                      )}
                    </div>
                  );
                })}
                <p className="text-xs text-slate-500">Only one connection can be active. Switching does not delete or log out the saved connection.</p>
              </div>
            )}

            {c.kind === "managed" && managedReady && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><Check className="h-4 w-4" /> Calling service configured</p>
                {c.details?.message && <p className="mt-2 text-xs leading-relaxed text-emerald-800/80">{c.details.message}</p>}
                {c.details?.public_url && <a className="mt-3 inline-flex text-sm font-semibold text-orange-700 hover:underline" href={c.details.public_url} target="_blank" rel="noreferrer">Open and test TalkID <ArrowRight className="ml-1 h-4 w-4" /></a>}
              </div>
            )}

            {c.kind === "knowledge" && c.status === "connected" && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><Check className="h-4 w-4" /> Website knowledge imported</p>
                {c.details?.message && <p className="mt-2 text-xs text-emerald-800/80">{c.details.message}</p>}
              </div>
            )}
          </div>
        );
      })}
      <p className="text-sm text-slate-500 sm:col-span-2">
        Connection status is verified by the server. Passwords, QR payloads, cookies and API secrets are never stored in this page.
      </p>
    </div>
  );
}

export function TestAndStart({ onGoLive, profile, dashboard = false }) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch("/wp-json/csai/v1/channels", { credentials: "same-origin", cache: "no-store" });
        if (response.status === 401) { window.location.href = "/login"; return; }
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Could not check connections.");
        if (!cancelled) { setChannels(data.channels || []); setError(""); }
      } catch (e) { if (!cancelled) { setError(e.message); setChannels([]); } }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    const timer = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);
  const active = (key) => {
    const channel = channels.find((c) => c.channel === key);
    return (key !== "whatsapp" || Boolean(channel?.details?.test_phone)) && channel?.status === "connected" && (channel.kind === "managed"
      ? Boolean(channel.details?.public_url)
      : Boolean(channel.methods?.some((m) => m.is_active && m.status === "connected")));
  };
  const knowledge = channels.some((c) => c.channel === "website" && c.status === "connected");
  const ready = !error && knowledge && (active("talkid") || active("whatsapp"));
  const whatsappChannel = channels.find((c) => c.channel === "whatsapp");
  const phoneLabel = whatsappChannel?.details?.test_phone || "your verified connected WhatsApp number";
  const emailChannel = channels.find((c) => c.channel === "gmail");
  const emailLabel = emailChannel?.methods?.find((m) => m.is_active)?.details?.expected_identity || "your connected business email";
  const questions = [
    "Aap abhi mere liye kya kya kar sakte ho?",
    "आपको मेरे business के बारे में अभी क्या जानकारी है?",
    "What products or services does this business offer?",
    "मैं order कैसे कर सकता हूँ या service कैसे book कर सकता हूँ?",
    "Delivery kahan hoti hai aur kitna time lagta hai?",
    "What information do you still need from me?",
  ];
  const instructions = {
    talkid: [
      "Use your TalkID page for online customer support. Visitors can open the link and speak to your AI.",
      "For calls through a phone number, contact customer care to arrange setup. Ask the team about connecting your existing number.",
      "Allow microphone access, start the call and ask one business question at a time.",
    ],
    whatsapp: [
      "Your test message goes to " + phoneLabel + ", your active connected WhatsApp number.",
      "Use a different WhatsApp number for this test. The button opens WhatsApp on your phone or computer; press Send there.",
      "The receiving number must be the WhatsApp account connected to this business. A new business question should receive a knowledge-grounded reply within about one minute.",
    ],
    gmail: [
      "The button opens your mail app with " + emailLabel + " as recipient.",
      "The subject and a sample business question are prefilled. Review them and press Send in your mail app.",
      "Sending is done only by your mail app. A new business email should receive a knowledge-grounded reply within about one minute.",
    ],
    facebook: [
      "Connect your business Facebook page from the dashboard to enable testing.",
      "Once ready, use another Facebook account to send the page a business question.",
      "Connection and automatic replies are not configured yet.",
    ],
    instagram: [
      "Connect your business Instagram account from the dashboard to enable testing.",
      "Once ready, send a business question from a different Instagram account.",
      "Connection and automatic replies are not configured yet.",
    ],
    webstore: [
      "Website Update will let you request changes to your products, prices and stock.",
      "It needs a separate authorized store connection. Importing Website Knowledge does not allow website changes.",
      "This feature is not available yet. No website changes are made by this test page.",
    ],
  };
  const tests = [
    { key: "talkid", icon: PhoneCall, title: "Test AI call", desc: "Open your business calling page and speak to your AI." },
    { key: "whatsapp", icon: MessageCircle, title: "Test WhatsApp", desc: "Open WhatsApp and send a business question to your connected account." },
    { key: "gmail", icon: Mail, title: "Test Email", desc: "Send a business question to your connected mailbox." },
    { key: "facebook", icon: Facebook, title: "Test Facebook", desc: "Test the connected business page." },
    { key: "instagram", icon: Instagram, title: "Test Instagram", desc: "Test the connected business account." },
    { key: "webstore", icon: Globe, title: "Website Update", desc: "Future: update stock, prices and products using commands. Requires separate store-write access." },
  ];
  const runTest = async (key) => {
    const popup = key === "whatsapp" ? window.open("about:blank", "_blank") : null;
    setBusy(key);
    try {
      const response = await fetch("/wp-json/csai/v1/channels/" + key + "/test", {
        method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Could not run the connection check.");
      if (key === "whatsapp" && data.url) {
        if (popup) { popup.opener = null; popup.location.replace(data.url); }
        else window.location.assign(data.url);
      } else if (key === "gmail" && data.url) window.location.assign(data.url);
    } catch (e) { if (popup) popup.close(); toast.error(e.message); }
    finally { setBusy(""); }
  };
  return (
    <div className="space-y-6" data-testid="onboarding-step-4">
      <section aria-label="AI training status" className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-5 sm:p-6" data-testid="training-notice">
        <h3 className="font-display text-lg font-bold text-amber-950">Your AI is still learning</h3>
        <p className="mt-2 text-sm leading-relaxed text-amber-950">
          Your AI is not fully trained yet. For now, it can answer only some questions using the information you have shared. Add more information about your business to help it answer more questions. It may not know the answer to topics you have not taught it yet.
        </p>
      </section>
      <p className="text-sm text-slate-600">Only active connections can be tested. You can connect other channels later from your dashboard.</p>
      {error && <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-700">{error}</p>}
      <div className="space-y-4">
        {tests.map((t) => {
          const enabled = active(t.key) && !loading && !error;
          return (
            <div key={t.key} className="grid gap-4 md:grid-cols-3" data-testid={"test-row-" + t.key}>
            <div data-testid={"test-card-" + t.key} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5">
              <t.icon className={"h-6 w-6 " + (enabled ? "text-emerald-600" : "text-slate-400")} />
              <h3 className="font-display text-base font-bold text-slate-900">{t.title}</h3>
              <p className="flex-1 text-sm text-slate-500">{t.desc}</p>
              {enabled && t.key === "talkid" ? (
                <a href={channels.find((c) => c.channel === "talkid")?.details?.public_url} target="_blank" rel="noopener noreferrer"
                  className="rounded-xl bg-emerald-600 px-4 py-3 text-center text-sm font-semibold text-white hover:bg-emerald-700">Test AI call</a>
              ) : (
                <button type="button" disabled={!enabled || busy === t.key} onClick={() => runTest(t.key)}
                  className={"rounded-xl px-4 py-3 text-sm font-semibold " + (enabled ? "bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60" : "bg-slate-100 text-slate-500 cursor-not-allowed")}>
                  {loading ? "Checking..." : busy === t.key ? "Opening..." : enabled ? t.title : "Not configured"}
                </button>
              )}
            </div>
            <section aria-label={t.title + " instructions"} className="rounded-2xl border border-slate-200 bg-white p-5 md:col-span-2">
              <h4 className="font-display text-base font-bold text-slate-900">How to test</h4>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-600">
                {instructions[t.key].map((line) => <li key={line}>{line}</li>)}
              </ul>
            </section>
            </div>
          );
        })}
      </div>
      <section className="rounded-2xl border border-orange-200 bg-orange-50/40 p-5 sm:p-6" data-testid="shared-test-questions">
        <h3 className="font-display text-lg font-bold text-slate-900">Questions to try · ये सवाल पूछकर देखें</h3>
        <p className="mt-2 text-sm text-slate-600">English, हिंदी या Hinglish में पूछें। जवाब उपलब्ध business knowledge और उस channel की readiness पर निर्भर होगा।</p>
        <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-800">
          {questions.map((question) => <li key={question}>{question}</li>)}
        </ul>
        <p className="mt-4 text-xs text-slate-600">Connected WhatsApp और Email पर नया business-related message available knowledge से reply होगा। Personal या unrelated messages छोड़े जाएंगे।</p>
      </section>
      {!dashboard && <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-6 text-center">
        <PartyPopper className="mx-auto h-8 w-8 text-emerald-600" />
        <h3 className="mt-3 font-display text-xl font-bold">Finish setup and open your dashboard</h3>
        <p className="mt-2 text-sm text-slate-600">Channel permissions and automation are managed separately. Completing setup does not grant sending or website-editing permissions.</p>
        {!ready && <p className="mt-3 text-sm text-amber-800">Website knowledge and at least one active TalkID or WhatsApp connection are required.</p>}
        <button disabled={!ready || loading} onClick={onGoLive} data-testid="start-my-ai-employee-button"
          className="mt-5 rounded-xl bg-emerald-600 px-8 py-4 font-bold text-white disabled:opacity-50">Finish &amp; Open Dashboard</button>
      </div>}
    </div>
  );
}

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [confirmLive, setConfirmLive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    business_name: "", website_url: "", category: "", business_location: "",
    contact_number: "", support_email: "", preferred_language: "English",
  });
  useEffect(() => {
    fetch("/wp-json/csai/v1/business", { credentials: "same-origin" })
      .then(async (response) => {
        if (response.status === 401) {
          window.location.href = "/login";
          return null;
        }
        const data = await response.json();
        if (data?.business) {
          setProfile((current) => ({ ...current, ...data.business }));
          setStep(Math.max(0, Math.min(3, Number(data.business.onboarding_step || 1) - 1)));
        }
        return data;
      })
      .catch(() => toast.error("Could not load your saved setup."));
  }, []);

  const saveProfile = async (nextStep = step + 1) => {
    if (!profile.business_name.trim()) {
      toast.error("Please enter your business name.");
      return false;
    }
    setSaving(true);
    try {
      const response = await fetch("/wp-json/csai/v1/business", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...profile, onboarding_step: Math.max(1, Math.min(4, nextStep)) }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Could not save your setup.");
      setProfile((current) => ({ ...current, ...data.business }));
      return true;
    } catch (error) {
      toast.error(error.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    try {
      await fetch("/wp-json/csai/v1/logout", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
    } finally {
      localStorage.removeItem("csai_user");
      window.location.href = "/login";
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="onboarding-page">
      <header className="border-b border-slate-100 bg-white" data-testid="onboarding-header">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4 sm:px-6">
          <Logo size="sm" />
          <div className="flex items-center gap-4">
            <button
              data-testid="onboarding-save-exit-button"
              onClick={async () => {
                if (await saveProfile(step + 1)) {
                  toast.success("Progress saved");
                  window.location.href = "/";
                }
              }}
              className="text-sm font-medium text-slate-500 hover:text-slate-800"
            >
              Save and exit
            </button>
            <button
              type="button"
              data-testid="onboarding-logout-button"
              onClick={logout}
              className="text-sm font-semibold text-slate-700 hover:text-orange-700"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 md:py-12">
        {/* Progress */}
        <div data-testid="onboarding-progress" className="mb-10">
          <div className="flex items-center justify-between">
            {STEPS.map((label, i) => (
              <div key={label} className="flex flex-1 items-center last:flex-none">
                <button
                  data-testid={`progress-step-${i + 1}`}
                  onClick={() => i < step && setStep(i)}
                  className="flex flex-col items-center gap-2"
                >
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full border-2 font-display text-sm font-bold transition-all ${
                      i < step
                        ? "border-emerald-500 bg-emerald-500 text-white"
                        : i === step
                        ? "border-orange-500 bg-orange-500 text-white"
                        : "border-slate-200 bg-white text-slate-400"
                    }`}
                  >
                    {i < step ? <Check className="h-4 w-4" /> : i + 1}
                  </span>
                  <span className={`hidden text-xs font-semibold sm:block ${i === step ? "text-slate-900" : "text-slate-400"}`}>
                    {label}
                  </span>
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`mx-2 mb-0 h-0.5 flex-1 rounded sm:mb-6 ${i < step ? "bg-emerald-400" : "bg-slate-200"}`} />
                )}
              </div>
            ))}
          </div>
          <p className="mt-4 text-center text-sm font-medium text-slate-500 sm:hidden">
            Step {step + 1} of {STEPS.length} — {STEPS[step]}
          </p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="onboarding-step-title">
              {STEPS[step]}
            </h1>
            <div className="mt-6">
              {step === 0 && <Step1 profile={profile} setProfile={setProfile} />}
              {step === 1 && <Step2 profile={profile} setProfile={setProfile} />}
              {step === 2 && <Step3 />}
              {step === 3 && <TestAndStart profile={profile} onGoLive={() => setConfirmLive(true)} />}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-between border-t border-slate-200 pt-6">
          {step > 0 ? (
            <button
              data-testid="onboarding-back-button"
              onClick={() => setStep(step - 1)}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : <span />}
          {step < 3 && (
            <button
              data-testid="onboarding-continue-button"
              onClick={async () => {
                if (await saveProfile(step + 2)) {
                  setStep(step + 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-8 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-orange-700 disabled:cursor-wait disabled:opacity-60"
            >
              {saving ? "Saving..." : "Continue"} <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </main>

      <AlertDialog open={confirmLive} onOpenChange={setConfirmLive}>
        <AlertDialogContent data-testid="go-live-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Finish your business setup?</AlertDialogTitle>
            <AlertDialogDescription>
              Your saved connections will be available in your dashboard. Automatic sending and website updates require separate channel permissions and configuration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="go-live-cancel-button">Not yet</AlertDialogCancel>
            <AlertDialogAction
              data-testid="go-live-confirm-button"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  const response = await fetch("/wp-json/csai/v1/onboarding/complete", {
                    method: "POST",
                    credentials: "same-origin",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({}),
                  });
                  const data = await response.json();
                  if (!response.ok) throw new Error(data.message || "Could not finish onboarding.");
                  localStorage.setItem("csai_user", JSON.stringify(data.user));
                  toast.success("Your setup is complete.");
                  window.location.href = data.redirect_to || "/dashboard";
                } catch (error) {
                  toast.error(error.message);
                } finally {
                  setSaving(false);
                }
              }}
            >
              {saving ? "Finishing setup..." : "Open dashboard"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
